// ============================================================================
// APP METADATA
// ============================================================================
export const APP_NAME = 'iSDOFSolver';
export const APP_VERSION = '1.1.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
export { ORIENTATION_TYPES } from '../shared';

// ============================================================================
// TYPES
// ============================================================================

import type { ChartImage, LivePlotImageOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';

export type { ChartImage };

export type ChartKey = 'chart_load' | 'chart_disp' | 'chart_rot' | 'chart_reaction';

export const PRINT_CHART_ORDER: ChartKey[] = ['chart_load', 'chart_reaction', 'chart_disp', 'chart_rot'];

export const PRINT_CHART_ASPECT = {
  chart_load: 'wide',
  chart_disp: 'wide',
  chart_rot: 'wide',
  chart_reaction: 'wide'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 700,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 18 },
      title: { font: { size: 30 } },
      margin: { l: 10, r: 10, t: 80, b: 10 },
      xaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      yaxis: { title: { font: { size: 18 } }, tickfont: { size: 18 } },
      legend: { font: { size: 24 }, x: 0.5, xanchor: 'center', y: -0.36 }
    }
  },
  square: {
    width: 500,
    height: 500,
    scale: 2,
    layout: {
      font: { size: 17 },
      title: { font: { size: 30 } },
      margin: { l: 92, r: 30, t: 78, b: 120 },
      xaxis: { title: { font: { size: 19 } }, tickfont: { size: 15 } },
      yaxis: { title: { font: { size: 19 } }, tickfont: { size: 15 } },
      legend: { font: { size: 16 }, x: 0.5, xanchor: 'center', y: -0.34 }
    }
  }
};

// API payload type for the iSDOF solver app
// This is not same as ParsedValues because Backend uses something different
export type APIPayload = {
  title: string;
  L: string;
  W: string;
  Lo: string;
  orientation: number;
  weight: string;
  KLM: string;
  xsi: string;
  // CSV strings
  Rn: string;
  Xn: string;
  Re: string;
  Xe: string;
  Pa: string;
  td: string;
  d0: string;
  v0: string;
};

export type ResultSummary = RichSummary;

export interface APIResponse {
  plotData: Partial<Record<ChartKey, PlotSpec>>;
  summary: ResultSummary;
}

// Frontend state for this page after analysis. The API returns summary + plotData only;
// `charts` is generated later from mounted LivePlot components for print/save.
export type SDOFSolverResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

// ============================================================================
// URL PARAMETER MAPPING
// ============================================================================

/**
 * Mapping from URL parameter names to form field names
 * Used for parsing ?inp={...} query params
 */
export const URL_FIELD_MAP: Record<string, string> = {
  title: 'title',
  L: 'length',
  W: 'tributaryWidth',
  Lo: 'hingeDistance',
  orientation: 'orientation',
  weight: 'weight',
  xsi: 'damping',
  Rn: 'resistanceNL',
  Xn: 'deflectionNL',
  Re: 'resistanceLinear',
  Xe: 'deflectionLinear',
  KLM: 'klm',
  P: 'axialLoad',
  Pa: 'pressureHistory',
  td: 'timeHistory',
  d0: 'u0',
  v0: 'v0'
};

/**
 * Value mappings for URL params (e.g., '0' -> 'Vertical')
 * Re-export from shared constants - only includes orientation for this app
 */
export { URL_VALUE_MAPS } from '../shared';
